
import {defineComponent, ref} from "vue";
import CatalogBuildStatus from "@/components/layouts/catalog/CatalogBuildStatus.vue";
import ProductFilter from "@/components/layouts/catalog/product-filter/ProductFilter.vue";
import {MultilingualProductLabelRestDto, ProductFilterRestDto} from "@/api/pharma-cpc-mgmt/models";
import {generateUuid} from "@/helpers/functions/string";
import {generateProductFilterPreviewUrl} from "@/helpers/functions/route";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import DiscountPricelistSection from "@/components/layouts/catalog/discounts-pricelists/DiscountPriceListSection.vue";
import ProductLabel from "@/components/layouts/catalog/product-labels/ProductLabel.vue";
import {I18nContext} from "@/context/I18nContext";
import MultilingualSwitcher from "@/components/UI/MultilingualSwitcher.vue";
import BaseOffcanvasButtonOpen from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonOpen.vue";
import BaseOffcanvas from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvas.vue";
import {
  GlobalFilterExcludeConditionsRestDtoModel
} from "@/models/api/pharma-cpc-mgmt/GlobalFilterExcludeConditionsRestDtoModel";
import RenderedProductLabel from "@/components/layouts/catalog/product-labels/RenderedProductLabel.vue";
import {ColorSchemeRestDtoModel} from "@/models/api/pharma-cms-color-scheme/ColorSchemeRestDtoModel";
import {ColorSchemeRestService} from "@/services/rest/cms-color-scheme/ColorSchemeRestService";
import {ProductLabelRestService} from "@/services/rest/cpc-mgmt/ProductLabelRestService";
import {FormMixin} from "@/components/base/FormMixin";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {ProductFilterRestDtoModel} from "@/models/api/pharma-cpc-mgmt/ProductFilterRestDtoModel";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import {ProductFiltersRestService} from "@/services/rest/product-filters/ProductFiltersRestService";

export default defineComponent({
  name: "ProductFiltersDetail",
  mixins: [FormMixin],
  components: {
    GenericFormWrapper,
    RenderedProductLabel,
    BaseOffcanvas,
    BaseOffcanvasButtonOpen,
    MultilingualSwitcher,
    ProductLabel,
    DiscountPricelistSection,
    BaseTitle,
    ProductFilter,
    CatalogBuildStatus
  },
  props: ["mode"],
  data() {
    return {
      code: this.$route.params.code ?? generateUuid() as string,
      returnRouteObject: {name: 'product-filters'} as object,
      activeLanguage: I18nContext.getDefaultApiLanguageKey(),
      //Hack: and errors object has to be passed to the MultilingualSwitcher, but I can't use the errors object from the form,
      // because this switcher is not inside the form.
      languageSwitchErrors: ref([]),
      productFilter: ProductFilterRestDtoModel.createWithDefaults() as ProductFilterRestDto,
      productLabel: null as MultilingualProductLabelRestDto | null,
      colorScheme: ColorSchemeRestDtoModel.createWithDefaults(),
    };
  },
  mounted() {
    this.initialize();
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum
    },
    previewUrl(): string | null {
      if (this.code) {
        return generateProductFilterPreviewUrl(this.code as string);
      }

      return null;
    }
  },
  methods: {
    async doInitialize(): Promise<void> {
      // No implementation needed
    },
    async afterSave(): Promise<void> {
      if (this.mode === PageDetailModeEnum.NEW) {
        await this.$router.push(this.returnRouteObject);
      } else {
        window.scrollTo(0, 0);
        await handleSavedSuccessfully();
        await this.reloadContent();
      }
    },
    async doReload() {
      if (this.mode === PageDetailModeEnum.NEW) {
        this.productFilter = ProductFilterRestDtoModel.createWithDefaults();
      } else {
        this.productFilter = await ProductFiltersRestService.getInstance().findComplexProductFilter(this.code);
      }
      if (this.productFilter?.code === 'DELIVERY_BY_SHIPPING_SUPPORTED') {
        if (!this.productFilter.global_exclude_conditions) {
          this.productFilter.global_exclude_conditions = GlobalFilterExcludeConditionsRestDtoModel.createWithDefaults();
        }
      }
      this.colorScheme = await ColorSchemeRestService.getInstance().findColorScheme();
      await this.doReloadLabel();
    },
    async doSubmit(): Promise<void> {
      await ProductFiltersRestService.getInstance().saveComplexProductFilter(this.productFilter, this.code);
    },
    async doReloadLabel(): Promise<void> {
      const labelResult = await ProductLabelRestService.getInstance().getProductLabelForProductFilter(this.code);
      if (labelResult) {
        this.productLabel = labelResult.label;
      } else {
        this.productLabel = null;
      }
    },
    async reloadLabel(): Promise<void> {
      await this.performGenericReload(this.doReloadLabel);
    },
  }
});
